import React, { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { authApi, getToken, removeToken } from '../services/api';

interface User {
    user_name: string;
    user_email: string;
    business_name: string;
    user_type?: number;
}

interface AuthContextType {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    logout: () => Promise<void>;
    signup: (data: {
        user_name: string;
        business_name: string;
        user_email: string;
        contact_number: string;
        password: string;
        address: string;
        city: string;
    }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setTokenState] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Check for existing token on mount
        const existingToken = getToken();
        if (existingToken) {
            setTokenState(existingToken);
            // In a real app, you'd validate the token and fetch user data
            // For now, we'll just mark as authenticated
        }
        setIsLoading(false);
    }, []);

    const login = async (email: string, password: string) => {
        try {
            const response = await authApi.login(email, password);
            if (response.status && response.token) {
                setTokenState(response.token);
                setUser(response.userData as User);
            } else {
                throw new Error(response.message || 'Login failed');
            }
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    };

    const logout = async () => {
        try {
            await authApi.logout();
            setTokenState(null);
            setUser(null);
            removeToken();
        } catch (error) {
            console.error('Logout error:', error);
            // Still clear local state even if API call fails
            setTokenState(null);
            setUser(null);
            removeToken();
        }
    };

    const signup = async (data: {
        user_name: string;
        business_name: string;
        user_email: string;
        contact_number: string;
        password: string;
        address: string;
        city: string;
    }) => {
        try {
            const response = await authApi.signup(data);
            if (!response.status) {
                throw new Error(response.message || 'Signup failed');
            }
        } catch (error) {
            console.error('Signup error:', error);
            throw error;
        }
    };

    const value: AuthContextType = {
        user,
        token,
        isAuthenticated: !!token,
        isLoading,
        login,
        logout,
        signup,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export default AuthContext;
